import { useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useCreateTenant } from '@/hooks/useTenantQueries'
import { useNavigate } from 'react-router-dom'

// ============================================================================
// Validation Schema
// ============================================================================

const createTenantSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  contactEmail: z.string().email('Invalid email address'),
  contactPhone: z.string().min(10, 'Phone number must be at least 10 digits'),
  maxVMs: z.coerce.number().min(1, 'Must be at least 1').max(1000),
  maxStorage: z.coerce.number().min(10, 'Must be at least 10 GB').max(100000),
  maxNetwork: z.coerce.number().min(10, 'Must be at least 10 GB').max(100000),
})

type CreateTenantFormData = z.infer<typeof createTenantSchema>

// ============================================================================
// Component
// ============================================================================

interface CreateTenantFormProps {
  onSuccess?: () => void
}

export function CreateTenantForm({ onSuccess }: CreateTenantFormProps) {
  const navigate = useNavigate()
  const createTenant = useCreateTenant()
  // `createTenant.isPending` only disables the submit button after a render, so a fast
  // double-click (or Enter held while clicking) can fire `onSubmit` a second time before that
  // paints — this ref is set synchronously on the first call and read before anything else runs.
  const isSubmittingRef = useRef(false)

  const form = useForm<CreateTenantFormData>({
    resolver: zodResolver(createTenantSchema),
    defaultValues: {
      name: '',
      contactEmail: '',
      contactPhone: '',
      maxVMs: 10,
      maxStorage: 500,
      maxNetwork: 1000,
    },
  })

  const onSubmit = async (data: CreateTenantFormData) => {
    if (isSubmittingRef.current) {
      return
    }
    isSubmittingRef.current = true

    try {
      const tenant = await createTenant.mutateAsync({
        name: data.name,
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone,
        quotas: {
          maxVMs: data.maxVMs,
          maxStorage: data.maxStorage,
          maxNetwork: data.maxNetwork,
        },
      })

      if (onSuccess) {
        onSuccess()
      } else {
        // Routes are mounted under /admin; the bare /tenants/:id path does not exist and the
        // catch-all in App.tsx would have sent a successful create back to the /admin dashboard.
        navigate(`/admin/tenants/${tenant.id}`)
      }
    } catch {
      // Error handled by the mutation's own toast; unlatch so a corrected resubmit can go through.
      isSubmittingRef.current = false
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      <fieldset disabled={createTenant.isPending} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Basic Information</h3>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tenant Name</FormLabel>
                <FormControl>
                  <Input placeholder="Acme Corporation" {...field} />
                </FormControl>
                <FormDescription>
                  The name of the organization or tenant
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contactEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contact Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="contact@acme.com"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Primary email for tenant communication
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contactPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contact Phone</FormLabel>
                <FormControl>
                  <Input placeholder="+1 (555) 123-4567" {...field} />
                </FormControl>
                <FormDescription>
                  Primary phone number for tenant
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Resource Quotas */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Resource Quotas</h3>

          <FormField
            control={form.control}
            name="maxVMs"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Max Virtual Machines</FormLabel>
                <FormControl>
                  <Input type="number" min={1} max={1000} {...field} />
                </FormControl>
                <FormDescription>
                  Maximum number of VMs this tenant can create
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="maxStorage"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Max Storage (GB)</FormLabel>
                <FormControl>
                  <Input type="number" min={10} max={100000} {...field} />
                </FormControl>
                <FormDescription>
                  Maximum storage allocation in gigabytes
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="maxNetwork"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Max Network Bandwidth (GB/month)</FormLabel>
                <FormControl>
                  <Input type="number" min={10} max={100000} {...field} />
                </FormControl>
                <FormDescription>
                  Maximum monthly network transfer in gigabytes
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/tenants')}
            disabled={createTenant.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createTenant.isPending}>
            {createTenant.isPending && <Spinner className="mr-2" />}
            {createTenant.isPending ? 'Creating...' : 'Create Tenant'}
          </Button>
        </div>
      </fieldset>
      </form>
    </Form>
  )
}
